import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { all_routes } from '../../router/all_routes';
import type { AppDispatch, RootState } from '../../../core/data/redux/store';
import {
  fetchERequisitionList,
  deleteDraftERequisition,
  fetchERequisitionMaster,
  syncERequisition,
  ERequisitionRequest
} from '../../../core/data/redux/eRequisitionSlice';

import {
  getRequestStatusConfig,
  getRequestStatusLabel,
  SapRequestStatus
} from '../../../core/data/constants/requestStatus';

export const getRequestTypeLabel = (code?: string, masterList?: any[]) => {
  if (!code) return 'IT Request';
  const cleanCode = String(code).trim();
  if (Array.isArray(masterList)) {
    const found = masterList.find((m: any) => String(m.Value).trim() === cleanCode);
    if (found?.Ddtext) return String(found.Ddtext).trim();
  }
  return code;
};

export const getItemTypeLabel = (code?: string, masterList?: any[]) => {
  if (!code) return '-';
  const cleanCode = String(code).trim();
  if (Array.isArray(masterList)) {
    const found = masterList.find((m: any) => String(m.Value).trim() === cleanCode);
    if (found?.Ddtext) return String(found.Ddtext).trim();
  }
  return code;
};

export const getStatusBadgeClass = (statusStr?: string) => {
  const s = String(statusStr || '').trim();
  const config = getRequestStatusConfig(s);
  if (config.badgeClass && config.code === s.toUpperCase()) {
    return config.badgeClass;
  }
  const lower = s.toLowerCase();
  if (lower.includes('approved') || lower.includes('completed') || lower === 'a') return 'badge bg-success-subtle text-success border border-success-subtle';
  if (lower.includes('in-process') || lower.includes('pending') || lower === 'v' || lower === 't') return 'badge bg-warning-subtle text-warning border border-warning-subtle';
  if (lower.includes('reject') || lower === 'r' || lower === 'k' || lower === 'x') return 'badge bg-danger-subtle text-danger border border-danger-subtle';
  return 'badge bg-secondary-subtle text-secondary border border-secondary-subtle';
};

const ERequisitionITList: React.FC = () => {
  const routes = all_routes;
  const dispatch = useDispatch<AppDispatch>();
  const eState = useSelector((state: RootState) => state.eRequisitionState);

  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({ startDate: '', endDate: '', requestStatus: '', query: '' });
  const [page, setPage] = useState(1);
  const [deleteItem, setDeleteItem] = useState<ERequisitionRequest | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadList = () => {
    dispatch(fetchERequisitionList({ page: String(page), limit: '20', ...filters }));
  };

  useEffect(() => {
    dispatch(fetchERequisitionMaster({ page: 1, limit: 20 }));
    loadList();
  }, [dispatch, page]);

  const masterRaw = eState?.masterData?.data?.data || eState?.masterData?.data;
  const formMasterList = masterRaw?.Form_Type || [];

  const rawList = eState?.listData?.data?.data || eState?.listData?.data?.results || eState?.listData?.data || eState?.listData?.results || (Array.isArray(eState?.listData) ? eState.listData : []);
  const itemsList: ERequisitionRequest[] = Array.isArray(rawList) ? rawList : [];

  const handleClearFilter = () => {
    const reset = { startDate: '', endDate: '', requestStatus: '', query: '' };
    setFilters(reset); setPage(1);
    dispatch(fetchERequisitionList({ page: '1', limit: '20', ...reset }));
  };

  const formatDate = (val?: string) => {
    if (!val) return '-';
    try {
      if (val.includes('T') || val.includes('-')) {
        const d = new Date(val);
        if (!isNaN(d.getTime())) return d.toLocaleDateString('en-GB');
      }
    } catch { }
    return val;
  };

  const handleDeleteDraft = async () => {
    if (!deleteItem?.id) return;
    setIsDeleting(true);
    try {
      const res = await dispatch(deleteDraftERequisition({ id: deleteItem.id }));
      if (deleteDraftERequisition.fulfilled.match(res)) {
        toast.success('Draft deleted successfully');
        setDeleteItem(null);
        loadList();
      } else toast.error((res.payload as string) || 'Failed to delete');
    } catch (err: any) { toast.error(err?.message || 'Error deleting'); }
    finally { setIsDeleting(false); }
  };

  const stats = [
    { label: 'Total Requests', val: itemsList.length, color: 'primary', icon: 'ti-files' },
    { label: 'In-Process / Pending', val: itemsList.filter(i => /in-process|pending|new/i.test(i.Status || i.RequestStatus || i.requestStatus || i.status || '')).length, color: 'warning', icon: 'ti-clock' },
    { label: 'Approved / Completed', val: itemsList.filter(i => /approved|completed/i.test(i.Status || i.RequestStatus || i.requestStatus || i.status || '')).length, color: 'success', icon: 'ti-circle-check' },
    { label: 'Drafts', val: itemsList.filter(i => /draft/i.test(i.Status || i.RequestStatus || i.requestStatus || i.status || '')).length, color: 'secondary', icon: 'ti-file-pencil' }
  ];

  const handleSync = async () => {
    try {
      const res = await dispatch(syncERequisition({}));
      if (syncERequisition.fulfilled.match(res)) {
        toast.success(res.payload?.message || 'Requisitions synced successfully');
        loadList();
      } else {
        toast.error((res.payload as string) || 'Failed to sync');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error syncing requisitions');
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div>
            <h3 className="page-title mb-1 text-dark fw-bold"><i className="ti ti-devices me-2 text-danger"></i>E-Requisition (IT)</h3>
            <nav><ol className="breadcrumb mb-0">
              <li className="breadcrumb-item"><Link to={routes.employeeDashboard || '#'}>Dashboard</Link></li>
              <li className="breadcrumb-item active">E-Requisition (IT)</li>
            </ol></nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowFilter(!showFilter)}>
              <i className={`ti ti-filter-${showFilter ? 'off' : 'check'} me-1`}></i>{showFilter ? 'Hide Filters' : 'Filters'}
            </button>
            <button className="btn btn-outline-primary btn-sm" onClick={loadList} disabled={eState.loading === 'pending'}>
              <i className={`ti ti-refresh ${eState.loading === 'pending' ? 'spin' : ''} me-1`}></i>Refresh
            </button>
            <button className="btn btn-outline-info btn-sm" onClick={handleSync} disabled={eState.action === 'sync' && eState.loading === 'pending'}>
              <i className={`ti ti-reload ${(eState.action === 'sync' && eState.loading === 'pending') ? 'spin' : ''} me-1`}></i>Sync
            </button>
            <Link to={routes.eRequisitionIT.create} className="btn btn-danger btn-sm">
              <i className="ti ti-plus me-1"></i>Create IT Request
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="row g-3 mb-4">
          {stats.map((s, idx) => (
            <div className="col-md-3" key={idx}>
              <div className="card border-0 shadow-sm rounded-3">
                <div className="card-body p-3 d-flex align-items-center justify-content-between">
                  <div><span className="text-muted small fw-medium d-block">{s.label}</span><h4 className={`fw-bold text-${s.color} mb-0`}>{s.val}</h4></div>
                  <div className={`avatar avatar-md bg-${s.color}-subtle text-${s.color} rounded-circle d-flex align-items-center justify-content-center`}><i className={`ti ${s.icon} fs-20`}></i></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        {showFilter && (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <form onSubmit={(e) => { e.preventDefault(); setPage(1); loadList(); }}>
                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label fw-semibold small">Search Query</label>
                    <input type="text" className="form-control form-control-sm" placeholder="Ref No or Emp ID..." value={filters.query} onChange={e => setFilters({ ...filters, query: e.target.value })} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold small">Request Status</label>
                    <select className="form-select form-select-sm" value={filters.requestStatus} onChange={e => setFilters({ ...filters, requestStatus: e.target.value })}>
                      <option value="">All Statuses</option>
                      <option value="In-Process">In-Process / Pending</option>
                      <option value="Approved">Approved / Completed</option>
                      <option value="Rejected">Rejected</option>
                      <option value="Draft">Draft</option>
                    </select>
                  </div>
                  <div className="col-md-3"><label className="form-label fw-semibold small">Start Date</label><input type="date" className="form-control form-control-sm" value={filters.startDate} onChange={e => setFilters({ ...filters, startDate: e.target.value })} /></div>
                  <div className="col-md-3"><label className="form-label fw-semibold small">End Date</label><input type="date" className="form-control form-control-sm" value={filters.endDate} onChange={e => setFilters({ ...filters, endDate: e.target.value })} /></div>
                  <div className="col-12 d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleClearFilter}>Reset</button>
                    <button type="submit" className="btn btn-danger btn-sm">Apply</button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between">
            <h5 className="mb-0 fw-bold text-dark"><i className="ti ti-list me-2 text-danger"></i>IT Equipment & Portal Requests</h5>
            <span className="badge bg-light text-dark border">{itemsList.length} Records</span>
          </div>
          <div className="card-body p-0">
            {eState.loading === 'pending' ? (
              <div className="text-center py-5"><div className="spinner-border text-danger" role="status"></div></div>
            ) : itemsList.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light text-secondary">
                    <tr><th>Ref No</th><th>Status</th><th>Request Form Type</th><th>Creation Date</th><th>Approver</th><th>Remarks</th><th className="text-end">Actions</th></tr>
                  </thead>
                  <tbody>
                    {itemsList.map((item, idx) => {
                      const refNo = item.ReferenceNumber || item.referenceNo || `REQ-${item.id || idx}`;
                      const statusVal = item.RequestStatus || item.requestStatus || item.Status || item.status || 'P';
                      const formTypeLabel = item.SelectRequestFromTypeText || getRequestTypeLabel(item.SelectRequestFromType, formMasterList) || item.itFormType || 'IT Request';
                      const createdDate = formatDate(item.RequestCreationDate || item.requestCreationDate || item.createdAt || item.created_at);
                      const empUser = item.EmpID || '-';
                      const approver = item.ApproverName ? (item.ApproverUserID ? `${item.ApproverName} (${item.ApproverUserID})` : item.ApproverName) : (item.lastApprover || item.ApproverUserID || '-');
                      const rem = item.Remarks || item.HRemarks || item.remarks || '-';
                      const isDraft = String(statusVal).toLowerCase().includes('draft') || String(item.Status).toLowerCase().includes('draft');

                      return (
                        <tr key={item.id || refNo || idx}>
                          <td className="fw-bold text-dark">{refNo}</td>
                          <td><span className={getStatusBadgeClass(statusVal)}>{getRequestStatusLabel(statusVal)}</span></td>
                          <td className="fw-medium text-dark">{formTypeLabel}</td>
                          <td className="text-muted">{createdDate}</td>
                          <td>{approver}</td>
                          <td className="text-truncate" style={{ maxWidth: '160px' }}>{rem}</td>
                          <td className="text-end">
                            <div className="d-flex align-items-center justify-content-end gap-1">
                              <Link to={routes.eRequisitionIT.details.replace(':id', refNo)} className="btn btn-xs btn-outline-danger px-2">Details</Link>
                              {isDraft && <button className="btn btn-xs btn-outline-secondary text-danger px-2" onClick={() => setDeleteItem(item)}><i className="ti ti-trash"></i></button>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-5"><p className="text-muted">No Requisitions Found</p></div>
            )}
          </div>
        </div>

        {/* Delete Modal */}
        {deleteItem && (
          <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content border-0 shadow">
                <div className="modal-body text-center p-4">
                  <i className="ti ti-alert-triangle text-danger fs-32 mb-2 d-block"></i>
                  <h6>Delete Draft #{deleteItem.ReferenceNumber || deleteItem.id}?</h6>
                  <p className="text-muted small">This action cannot be undone.</p>
                  <div className="d-flex justify-content-center gap-2 mt-3">
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setDeleteItem(null)} disabled={isDeleting}>Cancel</button>
                    <button className="btn btn-sm btn-danger" onClick={handleDeleteDraft} disabled={isDeleting}>{isDeleting ? 'Deleting...' : 'Delete'}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ERequisitionITList;
